import React, { useState } from 'react';
import { bookingAPI, eventAPI } from '../services/api';

function CustomerLookup() {
  const [customerId, setCustomerId] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState({});

  const handleLookup = async () => {
    if (!customerId.trim()) {
      setError('Please enter a customer ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setBookings([]);
      
      // First get all events to check their bookings
      const eventsResponse = await eventAPI.getAll();
      const allEvents = eventsResponse.data;
      
      // Store events by ID for quick lookup
      const eventsMap = {};
      allEvents.forEach(event => {
        eventsMap[event.id || event.event_id] = event;
      });
      setEvents(eventsMap);
      
      // Get bookings for each event and filter by customer
      const customerBookings = [];
      
      for (const event of allEvents) {
        try {
          const bookingsResponse = await eventAPI.getBookings(event.id || event.event_id);
          if (bookingsResponse.data && Array.isArray(bookingsResponse.data)) {
            const eventBookings = bookingsResponse.data.filter(
              booking => booking.customer_id === customerId
            );
            customerBookings.push(...eventBookings);
          }
        } catch (err) {
          console.error(`Failed to get bookings for event ${event.id}:`, err);
        }
      }
      
      setBookings(customerBookings);
      
      if (customerBookings.length === 0) {
        setError('No bookings found for this customer ID');
      }
    } catch (err) {
      setError('Failed to lookup customer bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setLoading(true);
      await bookingAPI.cancel(bookingId, customerId, 'Customer requested cancellation');
      
      // Refresh bookings
      handleLookup();
    } catch (err) {
      setError('Failed to cancel booking');
      console.error(err);
      setLoading(false);
    }
  };

  const handleUpgrade = async (bookingId, currentType) => {
    const newType = prompt(`Enter new ticket type to upgrade from ${currentType}:`);
    if (!newType) return;

    try {
      setLoading(true);
      await bookingAPI.upgrade(bookingId, customerId, newType);
      
      // Refresh bookings
      handleLookup();
    } catch (err) {
      setError('Failed to upgrade booking');
      console.error(err);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="customer-lookup-container">
      <div className="page-header">
        <h1>Customer Lookup</h1>
        <p>Search for customer bookings by Customer ID</p>
      </div>

      <div className="lookup-form">
        <div className="form-group">
          <label className="form-label">Customer ID</label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <input
              type="text"
              className="form-input"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Enter customer ID"
              onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
              style={{ flex: 1 }}
            />
            <button 
              className="btn btn-primary"
              onClick={handleLookup}
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {bookings.length > 0 && (
        <div className="bookings-list">
          <h2>Customer Bookings ({bookings.length})</h2>
          
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Event</th>
                  <th>Ticket Type</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Booking Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.booking_id || booking.id}>
                    <td className="monospace">
                      {(booking.booking_id || booking.id || '').substring(0, 8)}...
                    </td>
                    <td>
                      {events[booking.event_id]?.name || booking.event_name || 'Unknown Event'}
                    </td>
                    <td>
                      <span className={`ticket-badge ${(booking.ticket_type || '').toLowerCase()}`}>
                        {booking.ticket_type}
                      </span>
                    </td>
                    <td>{booking.quantity}</td>
                    <td>
                      <span className={`status-badge ${(booking.status || 'confirmed').toLowerCase()}`}>
                        {booking.status || 'Confirmed'}
                      </span>
                    </td>
                    <td>{formatDate(booking.created_at || booking.timestamp || new Date())}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleUpgrade(booking.booking_id || booking.id, booking.ticket_type)}
                          disabled={loading}
                        >
                          Upgrade
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleCancel(booking.booking_id || booking.id)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style jsx>{`
        .customer-lookup-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .page-header h1 {
          margin: 0 0 0.5rem 0;
        }

        .page-header p {
          color: #666;
          margin: 0;
        }

        .lookup-form {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .bookings-list h2 {
          margin-bottom: 1rem;
        }

        .monospace {
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
        }

        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
        }

        .btn-warning {
          background-color: #ffc107;
          color: #000;
        }

        .btn-warning:hover {
          background-color: #e0a800;
        }

        .btn-danger {
          background-color: #dc3545;
        }

        .btn-danger:hover {
          background-color: #c82333;
        }

        .status-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-badge.confirmed {
          background-color: #d4edda;
          color: #155724;
        }

        .status-badge.cancelled {
          background-color: #f8d7da;
          color: #721c24;
        }

        .status-badge.upgraded {
          background-color: #fff3cd;
          color: #856404;
        }
      `}</style>
    </div>
  );
}

export default CustomerLookup;